import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  LocalShipping as DeliveryIcon,
  GetApp as DownloadIcon,
  DateRange as DateIcon,
  Store as StoreIcon,
  Filter as FilterIcon,
  Assessment as ReportsIcon

} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import { cacheEndpoints } from '../../utils/apiCache';
import { isStoreAdmin, isAllStoresAdmin } from '../../utils/roles';
import { useTheme, useMediaQuery } from '@mui/material';

// Large date ranges can take several minutes to build the Excel export
const REPORT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

const DeliveryReport = () => {
  const { state, actions } = useApp();
  const { user } = state;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Report filters state
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    storeId: 'all'
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [storeDetails, setStoreDetails] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const initializePage = async () => {
      setPageLoading(true);
      
      // Set default date range (yesterday only)
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      setFilters(prev => ({
        ...prev,
        startDate: yesterday.toISOString().split('T')[0],
        endDate: yesterday.toISOString().split('T')[0]
      }));

      // Initialize based on user role
      try {
        if (isStoreAdmin(user?.role)) {
          await fetchStoreDetails();
        } else if (isAllStoresAdmin(user?.role)) {
          await fetchStores();
        }
      } catch (error) {
        console.error('Error initializing page:', error);
      } finally {
        setPageLoading(false);
      }
    };

    if (user) {
      initializePage();
    }
  }, [user]);

  const fetchStoreDetails = async () => {
    if (!user?.linkedStore) return;
    
    try {
      const response = await api.get(`/stores/${user.linkedStore}`);
      if (response.data.success) {
        setStoreDetails(response.data.store);
        // For store admin, set the store filter to their store
        setFilters(prev => ({
          ...prev,
          storeId: response.data.store.branchId || response.data.store.id
        }));
      }
    } catch (error) {
      console.error('Error fetching store details:', error);
      setError('Failed to load store information');
    }
  };

  const fetchStores = async () => {
    try {
      const storesData = await cacheEndpoints.stores.getAll();
      setStores(storesData);
    } catch (error) {
      console.error('Error fetching stores:', error);
      actions.showSnackbar('Error fetching stores', 'error');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: value
      };

      // Real-time validation for date range
      if ((field === 'startDate' || field === 'endDate') && newFilters.startDate && newFilters.endDate) {
        const startDate = new Date(newFilters.startDate);
        const endDate = new Date(newFilters.endDate);

        if (startDate > endDate) {
          setError('Start date must be before end date');
        } else {
          const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);
          if (daysDiff > 31) {
            setError('Date range cannot exceed 1 month (31 days)');
          } else {
            setError(''); // Clear error if validation passes
          }
        }
      }

      return newFilters;
    });
  };

  const resetFilters = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    setFilters({
      startDate: yesterday.toISOString().split('T')[0],
      endDate: yesterday.toISOString().split('T')[0],
      storeId: user?.role === 'store_admin' ? (storeDetails?.branchId || storeDetails?.id || 'all') : 'all'
    });
    setError('');
  };

  const isDateRangeValid = () => {
    if (!filters.startDate || !filters.endDate) return false;

    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);

    if (startDate > endDate) return false;

    const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);
    return daysDiff <= 31;
  };



  const exportDeliveryReport = async () => {
    if (!isDateRangeValid()) {
      setError('Please select a valid date range');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Build query parameters
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        format: 'excel'
      };
      
      // Add store filter
      if (filters.storeId !== 'all') {
        if (user?.role === 'store_admin') {
          params.branchId = filters.storeId;
        } else {
          params.storeId = filters.storeId;
        }
      }
      
      console.log('Exporting delivery report with params:', params);
      
      // Make API call to download Excel
      const response = await api.get('/reports/delivery', {
        params,
        responseType: 'blob',
        timeout: REPORT_TIMEOUT_MS,
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const storeSuffix = filters.storeId !== 'all' ? `_store_${filters.storeId}` : '';
      
      link.download = `delivery_report_${timestamp}${storeSuffix}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      actions.showSnackbar('Delivery report exported successfully!', 'success');
      
    } catch (error) {
      console.error('Error exporting delivery report:', error);
      setError('Error exporting delivery report');
      actions.showSnackbar('Error exporting delivery report', 'error');
    } finally {
      setLoading(false);
    }
  };



  const getFilterSummary = () => {
    const summary = [];
    
    if (filters.startDate && filters.endDate) {
      if (filters.startDate === filters.endDate) {
        summary.push(`Date: ${new Date(filters.startDate).toLocaleDateString()}`);
      } else {
        summary.push(`Date Range: ${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`);
      }
    }
    
    if (filters.storeId !== 'all') {
      const store = stores.find(s => (s.branchId || s.id) === filters.storeId);
      summary.push(`Store: ${store?.storeName || filters.storeId}`);
    } else if (user?.role !== 'store_admin') {
      summary.push('All Stores');
    }
    
    return summary;
  };

  // Loading Screen
  if (pageLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          p: 3 
        }}
      >
        <CircularProgress size={60} sx={{ mb: 3, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ mb: 2, color: 'primary.main' }}>
          Loading Delivery Report
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center">
          Setting up your delivery tracking dashboard...
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Chip label="Fetching Store Data" size="small" variant="outlined" />
          <Chip label="Initializing Filters" size="small" variant="outlined" />
          <Chip label="Preparing Interface" size="small" variant="outlined" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <DeliveryIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Delivery Report
            </Typography>
          </Box>

          {/* Delivery Report Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1 }} />
          Delivery Report Filters
        </Typography>
        
        <Divider sx={{ my: 2 }} />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{width:'100%'}}>
          {/* First Row: Date Range (50%) and Store (50%) */}
          
          {/* Date Range - 50% width */}
          <Grid item xs={12} lg={6} sx={{width:'50%'}}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
              <DateIcon color="primary" />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Date Range
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: filters.endDate || new Date().toISOString().split('T')[0]
                }}
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: filters.startDate,
                  max: filters.startDate ? 
                    new Date(new Date(filters.startDate).getTime() + 31 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] :
                    new Date().toISOString().split('T')[0]
                }}
                fullWidth
              />
            </Box>
            {/* Date Range Helper Text */}
            {filters.startDate && filters.endDate && (
              <Box sx={{ mt: 1 }}>
                {isDateRangeValid() ? (
                  <Typography variant="caption" color="success.main">
                    ✓ Valid date range: {Math.ceil((new Date(filters.endDate) - new Date(filters.startDate)) / (1000 * 60 * 60 * 24))} days
                  </Typography>
                ) : (
                  <Typography variant="caption" color="error.main">
                    ✗ Date range exceeds 31 days or is invalid
                  </Typography>
                )}
              </Box>
            )}
          </Grid>

          {/* Store Selection - 50% width (always shown, disabled for store admin) */}
          <Grid item xs={12} md={6} sx={{width:'40%'}}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <StoreIcon color="primary" />
              <Typography variant="h6">
                Store
              </Typography>
            </Box>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Select Store</InputLabel>
              <Select
                value={filters.storeId}
                onChange={(e) => handleFilterChange('storeId', e.target.value)}
                label="Select Store"
                disabled={user?.role === 'store_admin'}
              >
                <MenuItem value="all">All Stores</MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.branchId || store.id}>
                    {store.storeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {user?.role === 'store_admin' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Store selection is limited to your assigned store
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Filter Summary */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Filters:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {getFilterSummary().map((filter, index) => (
              <Chip
                key={index}
                label={filter}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>

        {/* Download Button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={exportDeliveryReport}
            disabled={loading || !isDateRangeValid()}
            startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
            sx={{
              minWidth: 200,
              height: 50,
              fontSize: '1.1rem'
            }}
          >
            {loading ? 'Generating Report...' : 'Download Report'}
          </Button>
        </Box>
      </Paper>

    </Box>
  );
};

export default DeliveryReport;
